/**
 * DriverAlignmentPanel — strategic drivers at the S3 criteria surface.
 *
 * The strategy's drivers carry a weight and linked criteria ids, and until this
 * panel nothing consumed either ("strategy is in every meeting and an input to
 * none"). Rendered inside the S3 CriteriaEditor when the event is cycle-linked
 * and the cycle's strategy has active drivers, it answers two questions at the
 * moment criteria are being chosen:
 *
 *   1. Coverage — which drivers do the chosen criteria represent, which are
 *      unrepresented, and which chosen criteria serve no driver.
 *   2. Weights — driver weights can drive criteria weights, either once or
 *      LIVE. A person always establishes the connection (canon's human-authority
 *      posture); what they choose is whether it then holds. Either way only
 *      weights change on the existing criteria set — never adds or removes a
 *      criterion, so workflow criteria governance locks cannot be violated.
 *
 * WHY THE LIVE LINK EXISTS (defect, 2026-09-02). "Apply proposed weights" was a
 * one-time copy. Afterwards the strategy could be re-weighted, a driver
 * deactivated, or a criterion unlinked, and the session went on scoring against
 * a reading of the strategy that no longer existed — with nothing on screen
 * saying where those weights came from. The drivers had, in effect, become
 * permanent criteria weights of unknown origin. Linked weights follow the
 * strategy until someone unlinks them, and unlinking leaves the weights exactly
 * where they are: it stops them following, it does not revert them.
 *
 * Links are edited here too (the store's link/unlink mutators had no callers):
 * only attribute-keyed criteria are offered — a session-authored `crit_*` id is
 * meaningless outside this event and must not be written into the strategy.
 */

import { useEffect, useMemo, useState } from 'react';
import { ArrowRight } from 'lucide-react';
import { useSessionEngineStore } from '../store';
import { useStrategyStore } from '../../../stores/prioritization/strategyStore';
import { useDegapProcessStore } from '../../../stores/prioritization/degapProcessStore';
import {
  computeDriverCriteriaCoverage,
  proposeCriteriaWeightsFromDrivers,
} from '../../../lib/driverCriteriaAlignment';
import { strategyIdForCycle } from '../../../lib/strategy/cycleStrategy';
import {
  panelInset,
  LABEL_MONO,
  btnPrimary,
  btnSecondary,
  status,
} from '../../../components/mono/primitives';
import { Select } from '../../../components/ds';
import type { CriterionDef } from '../types';

const EMPTY_CRITERIA: CriterionDef[] = [];

export function DriverAlignmentPanel() {
  const event = useSessionEngineStore((s) => s.event);
  const setCriteria = useSessionEngineStore((s) => s.setCriteria);
  const setDriverWeightLink = useSessionEngineStore((s) => s.setDriverWeightLink);

  const cycleId = event?.contextMode === 'cycle_linked' ? event.cycleId : undefined;
  const cycle = useDegapProcessStore((s) =>
    cycleId ? s.cycles.find((c) => c.id === cycleId) : undefined,
  );
  const strategies = useStrategyStore((s) => s.strategies);
  const fetchStrategies = useStrategyStore((s) => s.fetchFromSupabase);
  const linkCriteriaToDriver = useStrategyStore((s) => s.linkCriteriaToDriver);
  const unlinkCriteriaFromDriver = useStrategyStore((s) => s.unlinkCriteriaFromDriver);

  useEffect(() => {
    if (cycleId) void fetchStrategies();
  }, [cycleId, fetchStrategies]);

  const strategy = useMemo(() => {
    if (!cycleId) return undefined;
    const id = strategyIdForCycle(cycleId, cycle?.strategyRef, Object.values(strategies));
    return id ? strategies[id] : undefined;
  }, [cycleId, cycle?.strategyRef, strategies]);

  const criteria = event?.criteria ?? EMPTY_CRITERIA;
  const coverage = useMemo(
    () => (strategy ? computeDriverCriteriaCoverage(strategy.strategicDrivers, criteria) : null),
    [strategy, criteria],
  );
  const proposal = useMemo(
    () => (strategy ? proposeCriteriaWeightsFromDrivers(strategy.strategicDrivers, criteria) : null),
    [strategy, criteria],
  );

  const [showProposal, setShowProposal] = useState(false);

  const linked = !!event?.driverWeightLink && event.driverWeightLink.strategyId === strategy?.id;
  const s3Locked = event?.steps.S3.runStatus === 'complete';

  /**
   * The link itself. While it holds, the strategy is the source of truth for
   * criteria weights, so any change on either side — a driver re-weighted or
   * deactivated upstream, a criterion linked or unlinked here, a criterion
   * added to the set — flows through on the next render.
   *
   * Two things keep this from looping: it writes only when the proposal
   * actually differs from what is already set, and the proposal is derived from
   * the same normalization the write produces, so one pass reaches a fixed
   * point. It also stands down entirely once S3 is locked — a completed
   * collaborative step's weights are evidence, and evidence does not move
   * underneath the room that produced it.
   */
  useEffect(() => {
    if (!linked || s3Locked || !proposal) return;
    const drifted = proposal.proposals.some((pr) => pr.currentWeight !== pr.proposedWeight);
    if (!drifted) return;
    const weightById = new Map(proposal.proposals.map((pr) => [pr.id, pr.proposedWeight]));
    setCriteria(criteria.map((c) => ({ ...c, weight: weightById.get(c.id) ?? c.weight })));
  }, [linked, s3Locked, proposal, criteria, setCriteria]);

  if (!event || !cycleId || !strategy || !coverage || coverage.activeDriverCount === 0) return null;
  const nameOf = (id: string) => criteria.find((c) => c.id === id)?.name ?? id;
  const driverNameOf = (id: string) =>
    strategy.strategicDrivers.find((d) => d.id === id)?.name ?? id;
  const hasAuthored = criteria.some((c) => c.id.startsWith('crit_'));

  function applyProposal(alsoLink: boolean) {
    if (!proposal || s3Locked || !strategy) return;
    const weightById = new Map(proposal.proposals.map((p) => [p.id, p.proposedWeight]));
    // Weights only — same ids, same order. Never adds or removes a criterion.
    setCriteria(criteria.map((c) => ({ ...c, weight: weightById.get(c.id) ?? c.weight })));
    if (alsoLink) {
      setDriverWeightLink({ strategyId: strategy.id, linkedAt: new Date().toISOString() });
    }
    setShowProposal(false);
  }

  /**
   * Release the link WITHOUT touching the weights. The room keeps whatever the
   * strategy last gave it and owns it from here — reverting would discard a
   * decision nobody asked to undo.
   */
  function unlinkWeights() {
    setDriverWeightLink(undefined);
    setShowProposal(false);
  }

  return (
    <div {...panelInset('p-3 space-y-3')}>
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <span className={LABEL_MONO}>Strategic drivers · {strategy.name || 'Untitled strategy'}</span>
        <span
          {...status(
            coverage.representedCount === coverage.activeDriverCount ? 'green' : 'mustard',
          )}
        >
          {coverage.representedCount}/{coverage.activeDriverCount} represented ·{' '}
          {coverage.representedDriverWeight}/{coverage.totalActiveDriverWeight} weight
        </span>
      </div>

      <ul className="space-y-1.5">
        {coverage.rows.map((row) => {
          const driver = strategy.strategicDrivers.find((d) => d.id === row.driverId);
          const linkable = criteria.filter(
            (c) => !c.id.startsWith('crit_') && !driver?.linkedCriteriaIds.includes(c.id),
          );
          const unlinkable = row.matchedCriteriaIds;
          const represented = row.matchedCriteriaIds.length > 0;
          return (
            <li key={row.driverId} className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <span {...status(represented ? 'green' : row.unlinked ? 'neutral' : 'coral', 'w-14 shrink-0')}>
                {row.weight}%
              </span>
              <span className="min-w-0 flex-1 text-sm text-[var(--text-pri)]">
                {row.driverName || 'Untitled driver'}
                <span className="ml-2 font-mono text-[0.72rem] text-[var(--text-ter)]">
                  {represented
                    ? row.matchedCriteriaIds.map(nameOf).join(' · ')
                    : row.unlinked
                      ? 'no linked criteria'
                      : 'not represented by chosen criteria'}
                </span>
              </span>
              {(linkable.length > 0 || unlinkable.length > 0) && (
                <span className="w-44 shrink-0">
                  <Select
                    size="sm"
                    aria-label={`Link or unlink criteria for ${row.driverName}`}
                    value=""
                    placeholder="Link / unlink…"
                    onChange={(e) => {
                      const [op, id] = e.target.value.split(':', 2);
                      if (!id) return;
                      if (op === 'link') linkCriteriaToDriver(strategy.id, row.driverId, id);
                      if (op === 'unlink') unlinkCriteriaFromDriver(strategy.id, row.driverId, id);
                    }}
                  >
                    {linkable.length > 0 && (
                      <optgroup label="Link">
                        {linkable.map((c) => (
                          <option key={c.id} value={`link:${c.id}`}>{c.name}</option>
                        ))}
                      </optgroup>
                    )}
                    {unlinkable.length > 0 && (
                      <optgroup label="Unlink">
                        {unlinkable.map((id) => (
                          <option key={id} value={`unlink:${id}`}>{nameOf(id)}</option>
                        ))}
                      </optgroup>
                    )}
                  </Select>
                </span>
              )}
            </li>
          );
        })}
      </ul>

      {coverage.unservedCriteriaIds.length > 0 && (
        <p className="font-mono text-[0.72rem] text-[var(--text-ter)]">
          Serving no driver: {coverage.unservedCriteriaIds.map(nameOf).join(' · ')}
        </p>
      )}
      {hasAuthored && (
        <p className="font-mono text-[0.72rem] text-[var(--text-ter)]">
          Authored criteria exist only in this event and cannot be linked to drivers.
        </p>
      )}

      {/*
        Provenance, stated where the weights are read. A room looking at these
        criteria can see that the numbers are the strategy's and not their own —
        which is precisely what the one-time copy could never say.
      */}
      {linked && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <span {...status('green')}>Weights linked</span>
          <span className="min-w-0 flex-1 font-mono text-[0.72rem] text-[var(--text-ter)]">
            {s3Locked
              ? 'S3 is locked — weights are held at the reading the room ran on.'
              : `Following ${strategy.name || 'this strategy'}: re-weight a driver, or link and unlink criteria above, and these weights follow.`}
          </span>
          <button {...btnSecondary()} onClick={unlinkWeights}>
            Unlink (keep weights)
          </button>
        </div>
      )}

      {proposal && !linked && !showProposal && (
        <button {...btnSecondary()} onClick={() => setShowProposal(true)}>
          Propose weights from drivers
        </button>
      )}

      {proposal && !linked && showProposal && (
        <div className="space-y-2">
          <ul className="space-y-1">
            {proposal.proposals.map((p) => {
              const zeroed = p.proposedWeight === 0;
              return (
                <li key={p.id} className="flex items-center gap-2 text-sm">
                  <span className="min-w-0 flex-1 text-[var(--text-pri)]">{p.name}</span>
                  <span className="font-mono text-[0.78rem] tabular-nums text-[var(--text-ter)]">
                    {p.currentWeight}%
                  </span>
                  <ArrowRight size={12} className="shrink-0 text-[var(--text-ter)]" />
                  <span
                    className={`w-10 text-right font-mono text-[0.78rem] tabular-nums ${
                      zeroed ? 'text-[var(--coral)]' : 'text-[var(--text-pri)]'
                    }`}
                  >
                    {p.proposedWeight}%
                  </span>
                </li>
              );
            })}
          </ul>
          {proposal.unrepresentedDriverIds.length > 0 && (
            <p className="font-mono text-[0.72rem] text-[var(--coral)]">
              Not flowing: {proposal.unrepresentedDriverIds.map(driverNameOf).join(' · ')} — link
              these drivers to chosen criteria or their weight stays outside the proposal.
            </p>
          )}
          {proposal.zeroedCriteriaIds.length > 0 && (
            <p className="font-mono text-[0.72rem] text-[var(--coral)]">
              Proposed 0%: {proposal.zeroedCriteriaIds.map(nameOf).join(' · ')} — a 0% weight
              removes a criterion's influence on scoring.
            </p>
          )}
          {s3Locked && (
            <p className="font-mono text-[0.72rem] text-[var(--text-ter)]">
              S3 was run collaboratively and is locked — unlock the step to apply.
            </p>
          )}
          <div className="flex flex-wrap gap-2">
            <button {...btnPrimary()} onClick={() => applyProposal(true)} disabled={s3Locked}>
              Link weights to strategy
            </button>
            <button {...btnSecondary()} onClick={() => applyProposal(false)} disabled={s3Locked}>
              Apply once
            </button>
            <button {...btnSecondary()} onClick={() => setShowProposal(false)}>
              Dismiss
            </button>
          </div>
          <p className="font-mono text-[0.72rem] text-[var(--text-ter)]">
            Linked weights keep following the strategy until you unlink them. Apply once copies
            them now and lets them drift.
          </p>
        </div>
      )}
    </div>
  );
}
